import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { toast } from 'sonner';
import { Queue } from '@rossum/api-client/queues';
import { QueueRule } from '@rossum/api-client/schemas';
import { notify } from '../../toaster';
import { RULES_QUERY_KEY } from './rulesQueryKeys';
import { useDeleteRule } from './useDeleteRule';
import { useDuplicateRule } from './useDuplicateRule';

type DeleteRuleParams = {
  rule: QueueRule;
};

export const hasDeleteAction = (
  queue: Queue | undefined,
  rule: QueueRule
): boolean => (queue ? rule.queues.length === 1 : true);

export const useDeleteRuleWithUndo = () => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { mutateAsync: deleteRule } = useDeleteRule();
  const { mutateAsync: duplicateRule } = useDuplicateRule();

  return useMutation({
    mutationFn: ({ rule }: DeleteRuleParams) =>
      deleteRule({ deletedRuleId: rule.id, skipInvalidation: true }),
    onSuccess: (_, { rule }) => {
      queryClient.invalidateQueries([RULES_QUERY_KEY]);

      const toastId = `undo-delete-rule-${rule.id}`;
      notify.default({
        title: intl.formatMessage({
          id: 'containers.message.ruleDeleteSuccess',
        }),
        toastActions: [
          {
            label: intl.formatMessage({
              id: 'components.toaster.actions.undo',
            }),
            onClick: () => {
              toast.dismiss(toastId);
              duplicateRule({ rule }).catch(() =>
                notify.error({
                  title: intl.formatMessage({
                    id: 'features.queueSettings.rules.list.delete.undo.notification.failure',
                  }),
                })
              );
            },
          },
        ],
        id: toastId,
      });
    },
    onError: () =>
      notify.error({
        title: intl.formatMessage({
          id: 'containers.message.ruleDeleteFailed',
        }),
      }),
  });
};
